import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Context global para proteção de navegação
const GlobalNavigationContext = createContext();

/**
 * Provider global para proteção de navegação em toda a aplicação
 */
export const GlobalNavigationProvider = ({ children }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [unsavedLocation, setUnsavedLocation] = useState('');

  // Registra que há mudanças não salvas
  const registerUnsavedChanges = useCallback((location = 'aplicação') => {
    setHasUnsavedChanges(true);
    setUnsavedLocation(location);
  }, []);

  // Remove o registro de mudanças não salvas
  const clearUnsavedChanges = useCallback(() => {
    setHasUnsavedChanges(false);
    setUnsavedLocation('');
  }, []);

  // Função para verificar se pode navegar
  const canNavigate = useCallback((customMessage) => {
    if (hasUnsavedChanges) {
      const message = customMessage || `Você tem alterações não salvas${unsavedLocation ? ` em ${unsavedLocation}` : ''}. Deseja sair mesmo assim?`;
      const userConfirmed = window.confirm(message);
      
      // Se o usuário confirmou a navegação, limpa o estado
      if (userConfirmed) {
        setHasUnsavedChanges(false);
        setUnsavedLocation('');
      }
      
      return userConfirmed;
    }
    return true;
  }, [hasUnsavedChanges, unsavedLocation]);

  // Limpa mudanças não salvas quando sai da ficha do paciente
  useEffect(() => {
    const handleLocationChange = () => {
      const currentPath = window.location.pathname;
      
      // Se não está mais na ficha do paciente, limpa o estado
      if (!currentPath.includes('/fichapaciente/') && hasUnsavedChanges) {
        setHasUnsavedChanges(false);
        setUnsavedLocation('');
      }
    };

    // Escuta mudanças na URL
    window.addEventListener('popstate', handleLocationChange);
    
    // Verifica a cada segundo se mudou de página (para navegação programática)
    const interval = setInterval(handleLocationChange, 1000);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      clearInterval(interval);
    };
  }, [hasUnsavedChanges]);

  // Proteção global contra saída da página
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const value = {
    hasUnsavedChanges,
    unsavedLocation,
    registerUnsavedChanges,
    clearUnsavedChanges,
    canNavigate
  };

  return (
    <GlobalNavigationContext.Provider value={value}>
      {children}
    </GlobalNavigationContext.Provider>
  );
};

/**
 * Hook para usar o contexto global de navegação
 */
export const useGlobalNavigation = () => {
  const context = useContext(GlobalNavigationContext);
  if (!context) {
    throw new Error('useGlobalNavigation deve ser usado dentro de GlobalNavigationProvider');
  }
  return context;
};